import type { Candle } from "../types";
import type { Timeframe } from "../lib/feed";

interface HeaderProps {
  candles: Candle[];
  live: boolean;
  onToggleLive: () => void;
  timeframe: Timeframe;
  onTimeframeChange: (tf: Timeframe) => void;
}

const TIMEFRAMES: Timeframe[] = ["M1", "M5", "M15", "H1"];

export default function Header({
  candles,
  live,
  onToggleLive,
  timeframe,
  onTimeframeChange,
}: HeaderProps) {
  const last = candles[candles.length - 1];
  const prev = candles[candles.length - 2];
  const change = last && prev ? last.close - prev.close : 0;
  const changePct = prev ? (change / prev.close) * 100 : 0;
  const up = change >= 0;

  return (
    <header className="flex h-14 shrink-0 items-center gap-5 border-b border-tv-border bg-tv-panel px-4">
      {/* Brand */}
      <div className="flex items-center gap-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded bg-tv-yellow font-bold text-tv-bg">
          Au
        </div>
        <div className="leading-tight">
          <div className="text-sm font-bold tracking-wide text-tv-text">
            GOLDSCOPE <span className="text-tv-yellow">JEV AI</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-tv-muted">
            Stage 1 · Frontend &amp; Backtester
          </div>
        </div>
      </div>

      {/* Symbol + price */}
      <div className="flex items-baseline gap-3">
        <span className="text-sm font-semibold text-tv-text">XAUUSD</span>
        {last && (
          <>
            <span className={`font-mono text-lg font-bold ${up ? "text-tv-green" : "text-tv-red"}`}>
              {last.close.toFixed(2)}
            </span>
            <span className={`font-mono text-xs ${up ? "text-tv-green" : "text-tv-red"}`}>
              {up ? "▲" : "▼"} {Math.abs(change).toFixed(2)} ({Math.abs(changePct).toFixed(3)}%)
            </span>
          </>
        )}
      </div>

      {/* Timeframe selector */}
      <div className="flex items-center gap-1 rounded border border-tv-border bg-tv-bg p-0.5">
        {TIMEFRAMES.map((tf) => (
          <button
            key={tf}
            onClick={() => onTimeframeChange(tf)}
            className={`rounded px-2.5 py-1 text-xs font-semibold transition ${
              timeframe === tf
                ? "bg-tv-blue text-white"
                : "text-tv-muted hover:text-tv-text"
            }`}
          >
            {tf}
          </button>
        ))}
      </div>

      <div className="ml-auto flex items-center gap-4">
        {/* Feed status */}
        <button
          onClick={onToggleLive}
          className="flex items-center gap-2 rounded border border-tv-border px-3 py-1.5 text-xs text-tv-muted transition hover:text-tv-text"
          title="Toggle simulated live feed"
        >
          <span
            className={`h-2 w-2 rounded-full ${
              live ? "animate-pulse bg-tv-green" : "bg-tv-muted"
            }`}
          />
          {live ? "LIVE FEED" : "PAUSED"}
        </button>

        <span className="hidden text-[10px] uppercase tracking-widest text-tv-muted md:inline">
          Cloudflare Pages · Edge
        </span>
      </div>
    </header>
  );
}
